/**
 * One-tap install of the "Save to Mindfull" Shortcut.
 *
 * The file is the shareable build of scripts/quick-save-shortcut/build.py
 * (no token inside; an import question asks for one), signed on a Mac with
 * `shortcuts sign --mode anyone` and committed at public/shortcuts/. Signing
 * only works on macOS, so the file is added by hand. See
 * docs/quick-save-shortcut.md.
 */

export const SHORTCUT_FILE_PATH = '/shortcuts/save-to-mindfull.shortcut';

let available: boolean | null = null;

/**
 * Whether the signed file is being served. Until it's added, the SPA rewrite in
 * vercel.json answers that path with index.html and a 200, so HTML means it's
 * missing. Only a yes is remembered, so a failed check is tried again next time.
 */
export async function isShortcutFileAvailable(fetchImpl: typeof fetch = fetch): Promise<boolean> {
  if (available) return true;
  try {
    const res = await fetchImpl(SHORTCUT_FILE_PATH, { method: 'HEAD', cache: 'no-store' });
    const type = res.headers.get('content-type') ?? '';
    available = res.ok && !type.includes('text/html');
  } catch {
    available = false;
  }
  return available;
}

/** For tests. */
export function resetShortcutFileCheck() {
  available = null;
}

/** Shortcuts runs on iPhone, iPad (which reports itself as a Mac) and macOS 12+. */
export function canInstallShortcuts(userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent): boolean {
  return /iPhone|iPad|iPod|Macintosh/.test(userAgent);
}

/**
 * Copies text that is still being made (a token being created). Safari only
 * allows clipboard writes during the tap itself, and the tap's permission is
 * gone after an await. A ClipboardItem that holds a promise keeps it, so the
 * write starts now and fills in when the text arrives.
 */
export async function copyPendingText(text: Promise<string>): Promise<void> {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/plain': text.then((t) => new Blob([t], { type: 'text/plain' })) }),
      ]);
      return;
    } catch {
      // Older browsers reject promise values; fall back to writeText below
    }
  }
  await navigator.clipboard.writeText(await text);
}
